import { useEffect, useRef } from "react";

import {
    Column,
    Row,
    Container,
    LanguageSelectForm,
    LogoTrello,
    useFooterContext,
    useIsLoggedIn,
    LinkButton,
    useAnalytics,
    AnalyticsAttributesProvider,
    useOnetrust,
    useIdentityUrl
} from "../../components";
import { UiFooterNavModel, UiNavLinkModel, UiLinkModel } from "../../models";
import { renderViewModel } from "../../render-view-model";

import { SocialNav } from "./SocialNav";
import {
    Footer,
    TabsList,
    SubNav,
    LogoAndLoginContainer,
    Divider
} from "./UiFooterNav.styles";

export type UiFooterNavProps = {
    readonly model: UiFooterNavModel;
};

const UiFooterNav = ({ model }: UiFooterNavProps) => {
    const isLoggedIn = useIsLoggedIn();
    const { sendTrackEvent } = useAnalytics();
    const {
        id,
        content: {
            tabs,
            subNavLinks,
            logInText,
            selectLanguageLabel,
            hideLanguageSelector = false,
            useEyebrowLogo
        }
    } = model;
    const { setFooterHeight } = useFooterContext();
    const footerRef = useRef<HTMLDivElement>(null);
    const identityLoginUrl = useIdentityUrl({ isLogin: true });

    const { privacyLink } = useOnetrust();

    useEffect(() => {
        function handleResize() {
            if (footerRef.current) {
                const height = footerRef.current.offsetHeight;
                setFooterHeight(height);
            }
        }
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [footerRef, setFooterHeight]);

    return (
        <AnalyticsAttributesProvider value={{ eventContainer: "globalFooter" }}>
            <Footer ref={footerRef}>
                <section>
                    <Container>
                        <Row verticalAlign="Top">
                            <Column
                                xs={{ align: "left", bottom: "none" }}
                                lg={{ span: "2" }}
                            >
                                <LogoAndLoginContainer>
                                    <LogoTrello
                                        appearance="white"
                                        eyebrow={useEyebrowLogo}
                                    />
                                    {!isLoggedIn && logInText && (
                                        <LinkButton
                                            url={identityLoginUrl}
                                            onClick={() =>
                                                sendTrackEvent(
                                                    "clickedFooterLogin",
                                                    {
                                                        event: "clicked",
                                                        eventComponent: "link",
                                                        eventContainer:
                                                            "globalFooter",
                                                        label: logInText,
                                                        labelEnglish:
                                                            "Log in - footer",
                                                        uuid: id,
                                                        href: "/login"
                                                    }
                                                )
                                            }
                                            data-uuid={id}
                                            testId="login"
                                        >
                                            {logInText}
                                        </LinkButton>
                                    )}
                                </LogoAndLoginContainer>
                            </Column>
                            <Column lg={{ span: "10" }}>
                                <TabsList>
                                    {tabs &&
                                        tabs.map(
                                            (tab: UiNavLinkModel, index) => (
                                                <li key={`tabs_${index}`}>
                                                    {renderViewModel(tab)}
                                                </li>
                                            )
                                        )}
                                </TabsList>
                            </Column>
                        </Row>
                    </Container>
                </section>
                <Divider />
                <section>
                    <Container>
                        <Row verticalAlign="center">
                            {!hideLanguageSelector && (
                                <Column
                                    xs={{ align: "left", top: "none" }}
                                    lg={{ span: "2", top: "compact" }}
                                >
                                    <LanguageSelectForm
                                        selectLanguageLabel={
                                            selectLanguageLabel
                                        }
                                    />
                                </Column>
                            )}
                            <Column
                                xs={{ top: "none" }}
                                lg={{
                                    span: `${hideLanguageSelector ? "9" : "7"}`,
                                    align: "left",
                                    top: "compact"
                                }}
                            >
                                <SubNav
                                    hideLanguageSelector={hideLanguageSelector}
                                >
                                    <ul>
                                        {privacyLink}
                                        {subNavLinks?.map(
                                            (item: UiLinkModel, index) => (
                                                <li key={`subNav_${index}`}>
                                                    {renderViewModel(item)}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </SubNav>
                            </Column>
                            <Column lg={{ span: "3", order: "12" }}>
                                <SocialNav />
                            </Column>
                        </Row>
                    </Container>
                </section>
            </Footer>
        </AnalyticsAttributesProvider>
    );
};

export default UiFooterNav;
